export const TAG_PRESET_COLORS = [
  '#c4c9d2',
  '#9aa1ad',
  '#5e6ad2',
  '#26b5ce',
  '#4cb782',
  '#f2c94c',
  '#f2994a',
  '#f7c8c8',
  '#eb5757',
]

// 3- or 6-digit RGB hex only. Alpha (#RGBA / #RRGGBBAA) is intentionally not
// supported; the HSV<->hex conversion in this file is RGB-only and the
// backend's Tag.color validator matches this same shape.
const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

export function isValidHex(value: string) {
  return HEX_RE.test(value)
}

export function clampHue(value: number) {
  return Math.max(0, Math.min(360, value))
}

export function clampPct(value: number) {
  return Math.max(0, Math.min(100, value))
}

export function hsvToHex(h: number, s: number, v: number) {
  const c = (v / 100) * (s / 100)
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = v / 100 - c

  let r = 0
  let g = 0
  let b = 0
  if (h < 60) [r, g, b] = [c, x, 0]
  else if (h < 120) [r, g, b] = [x, c, 0]
  else if (h < 180) [r, g, b] = [0, c, x]
  else if (h < 240) [r, g, b] = [0, x, c]
  else if (h < 300) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]

  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

export function readableTextColor(hex: string): '#000' | '#fff' {
  const normalized = hex.replace('#', '')
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map(c => c + c)
          .join('')
      : normalized.slice(0, 6)
  if (full.length !== 6) return '#000'
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  // YIQ luminance.
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 140 ? '#000' : '#fff'
}

export function hexToHsv(hex: string): { h: number; s: number; v: number } {
  const normalized = hex.replace('#', '')
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map(c => c + c)
          .join('')
      : normalized.slice(0, 6)
  if (full.length !== 6) return { h: 0, s: 0, v: 100 }
  const r = parseInt(full.slice(0, 2), 16) / 255
  const g = parseInt(full.slice(2, 4), 16) / 255
  const b = parseInt(full.slice(4, 6), 16) / 255
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return { h: 0, s: 0, v: 100 }
  }
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  const s = max === 0 ? 0 : (d / max) * 100
  const v = max * 100
  let h = 0
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h = h * 60
    if (h < 0) h += 360
  }
  return { h, s, v }
}
